import { useEffect, useRef } from "react";
import { useUnsavedChangesContext } from "../../contexts/UnsavedChangesContext";
import { checkAppVersion } from "../../utils/versionCheck";

// Minimum gap between refocus-triggered checks. Releases go out once or twice
// a week, so an hourly ceiling is plenty and keeps the request count trivial.
const REFOCUS_THROTTLE_MS = 60 * 60 * 1000;

/**
 * Renders nothing. Keeps the open tab on the latest deploy:
 *   1. one check on mount
 *   2. one check when the tab becomes visible again, at most hourly
 *
 * Reload is skipped while a form holds unsaved changes — the next check picks
 * it up once the user is done.
 */
export default function VersionWatcher() {
  const { hasUnsavedChanges } = useUnsavedChangesContext();
  const lastCheckRef = useRef(0);
  // Read the live dirty flag from inside the listener without re-subscribing.
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  useEffect(() => {
    lastCheckRef.current = Date.now();
    checkAppVersion();

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastCheckRef.current < REFOCUS_THROTTLE_MS) return;
      lastCheckRef.current = now;
      checkAppVersion({ skipReload: hasUnsavedChangesRef.current });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  return null;
}
